import { type ComponentProps, mergeProps, Show } from "solid-js";

/**
 * Space Academy's illustrated emblem — replaces the old mascot with a face.
 *
 * Theme-aware geometric/line illustration (tokens `--accent` / `--xp` /
 * `--muted`): the brand's open book with an ascending path and an
 * amber star. No faces or cartoon: the "character" comes from composition
 * variations by state (`mood`), designed for empty states, celebration
 * and feedback without breaking the professional tone.
 *
 * `mood` (API kept for compatibility with the call sites):
 *  - happy      → full emblem (default)
 *  - celebrate  → star at zenith + sparkles (end of lesson, badge)
 *  - think      → dotted path, star halfway (empty states)
 *  - wave       → double star, open path (welcome)
 *  - sleep      → closed book under a crescent moon (offline / no data)
 */
export type MascotMood = "happy" | "celebrate" | "think" | "wave" | "sleep";

interface MascotProps extends ComponentProps<"svg"> {
  mood?: MascotMood;
}

export function Mascot(rawProps: MascotProps) {
  const props = mergeProps({ mood: "happy" as MascotMood }, rawProps);

  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Emblema de Space Academy"
      {...props}
    >
      <Show when={props.mood !== "sleep"}>
        {/* Libro abierto (trazo, tinta de acento) */}
        <path
          d="M10 38.5c7.4-3 15.6-2.6 22 1.3c6.4-3.9 14.6-4.3 22-1.3v12.6c-7.4-3-15.6-2.6-22 1.3c-6.4-3.9-14.6-3.5-22-1.3Z"
          stroke="var(--accent)"
          stroke-width="3"
          stroke-linejoin="round"
          fill="color-mix(in oklab, var(--accent) 12%, transparent)"
        />
        {/* Lomo */}
        <path
          d="M32 40v12"
          stroke="var(--accent)"
          stroke-width="2.2"
          stroke-linecap="round"
          opacity="0.55"
        />
      </Show>

      <Show when={props.mood === "happy"}>
        <path
          d="M12 30C17 18.5 30 12 44 13.6"
          stroke="var(--accent)"
          stroke-width="3"
          stroke-linecap="round"
        />
        <path d="M51 6l2 5.2 5.2 2-5.2 2-2 5.2-2-5.2-5.2-2 5.2-2Z" fill="var(--xp)" />
      </Show>

      <Show when={props.mood === "celebrate"}>
        <path
          d="M14 30C19 18.5 30 11.5 42 11.8"
          stroke="var(--accent)"
          stroke-width="3"
          stroke-linecap="round"
        />
        {/* Star at zenith + sparkles */}
        <path d="M49 4l2.2 5.7 5.7 2.2-5.7 2.2-2.2 5.7-2.2-5.7-5.7-2.2 5.7-2.2Z" fill="var(--xp)" />
        <path
          d="M14 10l1.2 3.1 3.1 1.2-3.1 1.2-1.2 3.1-1.2-3.1-3.1-1.2 3.1-1.2Z"
          fill="var(--xp)"
          opacity="0.7"
        />
        <path
          d="M56 24l0.9 2.3 2.3 0.9-2.3 0.9-0.9 2.3-0.9-2.3-2.3-0.9 2.3-0.9Z"
          fill="var(--xp)"
          opacity="0.5"
        />
      </Show>

      <Show when={props.mood === "think"}>
        <path
          d="M12 31C16 22 24 15.8 34 13.8"
          stroke="var(--accent)"
          stroke-width="3"
          stroke-linecap="round"
          stroke-dasharray="1 7"
        />
        <path
          d="M41 8l1.7 4.4 4.4 1.7-4.4 1.7-1.7 4.4-1.7-4.4-4.4-1.7 4.4-1.7Z"
          fill="var(--xp)"
          opacity="0.85"
        />
      </Show>

      <Show when={props.mood === "wave"}>
        <path
          d="M12 30C17 18.5 30 12 44 13.6"
          stroke="var(--accent)"
          stroke-width="3"
          stroke-linecap="round"
        />
        <path d="M51 6l2 5.2 5.2 2-5.2 2-2 5.2-2-5.2-5.2-2 5.2-2Z" fill="var(--xp)" />
        <path
          d="M12 8l1.3 3.4 3.4 1.3-3.4 1.3-1.3 3.4-1.3-3.4-3.4-1.3 3.4-1.3Z"
          fill="var(--xp)"
          opacity="0.6"
        />
      </Show>

      <Show when={props.mood === "sleep"}>
        {/* Libro cerrado en reposo */}
        <path
          d="M12 42h34a6 6 0 0 1 6 6v2H18a6 6 0 0 1-6-6Z"
          stroke="var(--accent)"
          stroke-width="3"
          stroke-linejoin="round"
          fill="color-mix(in oklab, var(--accent) 12%, transparent)"
        />
        <path
          d="M18 42v-4a4 4 0 0 1 4-4h26a4 4 0 0 1 4 4v4"
          stroke="var(--accent)"
          stroke-width="3"
          stroke-linecap="round"
          opacity="0.45"
        />
        {/* Luna creciente */}
        <path
          d="M40 16a9.5 9.5 0 0 0 9 9.4A9.5 9.5 0 1 1 40 16Z"
          fill="var(--muted)"
          opacity="0.55"
        />
      </Show>
    </svg>
  );
}
